import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import SystemStatusBanner from '../../components/ui/SystemStatusBanner';
import QuickActionToolbar from '../../components/ui/QuickActionToolbar';
import UserDirectory from './components/UserDirectory';
import UserDetailsPanel from './components/UserDetailsPanel';
import AddUserModal from './components/AddUserModal';
import StatsOverview from './components/StatsOverview';
import { supabase } from '../../lib/supabase';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Icon from '../../components/AppIcon';

const TeamManagementUserAdministration = () => {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    administrators: 0,
    managers: 0
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    calculateStats();
  }, [users]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.select('*')
        ?.order('created_at', { ascending: false });

      if (error) throw error;

      setUsers(data || []);
      if (data?.length > 0 && !selectedUser) {
        setSelectedUser(data?.[0]);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = () => {
    const totalUsers = users?.length || 0;
    const activeUsers = users?.filter(u => u?.status === 'active')?.length || 0;
    const administrators = users?.filter(u => u?.role === 'administrator')?.length || 0;
    const managers = users?.filter(u => u?.role === 'manager')?.length || 0;

    setStats({
      totalUsers,
      activeUsers,
      administrators,
      managers
    });
  };

  const handleUserSelect = (user) => {
    setSelectedUser(user);
  };

  const handleUserUpdate = async (userId, updates) => {
    try {
      const { error } = await supabase
        ?.from('user_profiles')
        ?.update(updates)
        ?.eq('id', userId);

      if (error) throw error;

      // Update local state
      setUsers(prevUsers =>
        prevUsers?.map(user =>
          user?.id === userId ? { ...user, ...updates } : user
        )
      );

      if (selectedUser?.id === userId) {
        setSelectedUser(prev => ({ ...prev, ...updates }));
      }

      // Log activity
      await logActivity(userId, 'updated_user', { updates });
    } catch (error) {
      console.error('Error updating user:', error);
    }
  };

  const handleUserAdd = async (userData) => {
    try {
      // In a real implementation, this would create an auth.users entry
      // For now, we'll just refresh the list
      await fetchUsers();
      setIsAddUserModalOpen(false);
    } catch (error) {
      console.error('Error adding user:', error);
    }
  };

  const logActivity = async (userId, action, details) => {
    try {
      await supabase?.from('activity_logs')?.insert({
        user_id: userId,
        action,
        details,
        ip_address: '0.0.0.0'
      });
    } catch (error) {
      console.error('Error logging activity:', error);
    }
  };

  const filteredUsers = users?.filter(user => {
    const matchesSearch = user?.full_name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         user?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    const matchesRole = roleFilter === 'all' || user?.role === roleFilter;
    const matchesStatus = statusFilter === 'all' || user?.status === statusFilter;
    return matchesSearch && matchesRole && matchesStatus;
  });

  const roleOptions = [
    { value: 'all', label: 'All Roles' },
    { value: 'coordinator', label: 'Coordinator' },
    { value: 'supervisor', label: 'Supervisor' },
    { value: 'manager', label: 'Manager' },
    { value: 'administrator', label: 'Administrator' }
  ];

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'suspended', label: 'Suspended' }
  ];

  return (
    <>
      <Helmet>
        <title>Team Management & User Administration - TrackingManager</title>
        <meta name="description" content="Manage team members, assign roles, and control permissions for tracking entry and reporting access." />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        <SystemStatusBanner />

        <main className="pt-[60px]">
          <div className="container mx-auto px-18 md:px-24 lg:px-32 py-32 md:py-40">
            <div className="mb-32 md:mb-40">
              <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-12">
                Team Management
              </h1>
              <p className="font-body text-sm md:text-base text-muted-foreground max-w-3xl">
                Manage team members, assign role-based permissions, and control access to tracking entry and reporting features.
              </p>
            </div>

            <StatsOverview stats={stats} />

            <div className="bg-card rounded-lg shadow-elevation-2 border border-border p-24 md:p-32 mb-24 md:mb-32">
              <div className="flex flex-col md:flex-row gap-16 md:gap-24 items-start md:items-center justify-between mb-24">
                <div className="flex-1 w-full md:w-auto">
                  <Input
                    placeholder="Search by name or email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e?.target?.value)}
                  />
                </div>
                <div className="flex gap-12 w-full md:w-auto">
                  <Select
                    options={roleOptions}
                    value={roleFilter}
                    onChange={setRoleFilter}
                    placeholder="Filter by role"
                  />
                  <Select
                    options={statusOptions}
                    value={statusFilter}
                    onChange={setStatusFilter}
                    placeholder="Filter by status"
                  />
                </div>
                <Button
                  iconName="UserPlus"
                  onClick={() => setIsAddUserModalOpen(true)}
                >
                  Add User
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-24 md:gap-32">
              <div className="lg:col-span-1">
                <UserDirectory
                  users={filteredUsers}
                  selectedUser={selectedUser}
                  onUserSelect={handleUserSelect}
                  loading={loading}
                />
              </div>

              <div className="lg:col-span-2">
                {selectedUser ? (
                  <UserDetailsPanel
                    user={selectedUser}
                    onUpdate={handleUserUpdate}
                  />
                ) : (
                  <div className="bg-card rounded-lg shadow-elevation-2 border border-border p-40 text-center">
                    <Icon name="Users" size={48} color="var(--color-muted-foreground)" />
                    <p className="text-muted-foreground mt-16">Select a user to view details</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </main>

        <AddUserModal
          isOpen={isAddUserModalOpen}
          onClose={() => setIsAddUserModalOpen(false)}
          onAdd={handleUserAdd}
        />

        <QuickActionToolbar />
      </div>
    </>
  );
};

export default TeamManagementUserAdministration;